import { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CategoryScrollBar = ({ categories, selectedCategory, onCategoryChange }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Organize categories: Heritages and Tourist Places first, then others
  const organizedCategories = (() => {
    const priorityNames = ['Heritages', 'Tourist Places'];
    const priorityCategories = [];
    const otherCategories = [];
    
    categories.forEach(cat => {
      if (priorityNames.includes(cat.name)) {
        priorityCategories.push(cat);
      } else {
        otherCategories.push(cat);
      }
    });
    
    // Sort priority categories by the order in priorityNames
    priorityCategories.sort((a, b) => {
      return priorityNames.indexOf(a.name) - priorityNames.indexOf(b.name);
    });
    
    return [...priorityCategories, ...otherCategories];
  })();

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    // Use a small delay to ensure DOM is fully rendered before checking scroll
    const timer = setTimeout(() => {
      checkScroll();
      setIsInitialized(true);
    }, 0);

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => {
        clearTimeout(timer);
        container.removeEventListener('scroll', checkScroll);
      };
    }
    return () => clearTimeout(timer);
  }, []);

  // Recalculate arrow visibility when window resizes
  useEffect(() => {
    const handleResize = () => {
      checkScroll();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Warm pastel color palette for category text only (not backgrounds)
  const pastelTextColors = [
    'text-orange-600',
    'text-amber-600',
    'text-rose-600',
    'text-pink-600',
    'text-red-600',
    'text-yellow-600',
    'text-lime-600',
    'text-cyan-600',
  ];

  return (
    <div className="relative w-full flex items-center gap-2">
      {/* Left Arrow - Fixed position outside scroll area */}
      {isInitialized && showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="flex-shrink-0 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all duration-200 backdrop-blur-sm border border-gray-200/50"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}

      {/* Scrollable Container - flex-1 to take remaining space */}
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide py-2 flex-1"
        style={{
          scrollBehavior: 'smooth',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* All Categories Button */}
        <button
          onClick={() => onCategoryChange('')}
          className={`flex-shrink-0 px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap border-2 ${
            selectedCategory === ''
              ? 'bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/30 scale-105'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:scale-105'
          }`}
        >
          All
        </button>

        {/* Category Pills */}
        {organizedCategories.map((category) => {
          const isSelected = selectedCategory === category.id;

          return (
            <button
              key={category.id}
              onClick={() => onCategoryChange(category.id)}
              className={`flex-shrink-0 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap border-2 flex items-center gap-2.5 ${
                isSelected
                  ? 'bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/30 scale-105'
                  : `bg-white text-black border-gray-200 hover:border-gray-300 hover:scale-105`
              }`}
            >
              {/* Icon Background Circle */}
              {category.iconUrl && (
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-white/20' : 'bg-gray-100'
                  }`}
                >
                  <img
                    src={category.iconUrl}
                    alt={category.name}
                    className="w-4 h-4 object-contain"
                  />
                </div>
              )}
              <span>{category.name}</span>
            </button>
          );
        })}
      </div>

      {/* Right Arrow - Fixed position outside scroll area */}
      {isInitialized && showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="flex-shrink-0 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all duration-200 backdrop-blur-sm border border-gray-200/50"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      )}

      {/* Hide scrollbar styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default CategoryScrollBar;
